// Types
import type { APICRMBusinessSettingAppearance } from '@/types/api/items/crm/crm-business-settings'
import type { UiScalePopoverTransitionSlotProps, UiScalePopperModifier, UiScaleZoomMap } from '@/types/utils/ui/scale'

export const UI_SCALE_ZOOM: UiScaleZoomMap = {
	xsmall: 0.75,
	small: 0.85,
	medium: 1,
	large: 1.1,
	xlarge: 1.2
}

export const getUiScaleZoom = (uiScale: APICRMBusinessSettingAppearance['ui_scale']): number => UI_SCALE_ZOOM[uiScale] ?? UI_SCALE_ZOOM.medium

export const getDocumentUiZoom = (): number => {
	if (typeof document === 'undefined') return 1

	const fromStyle = Number.parseFloat(document.documentElement.style.zoom || '')
	if (Number.isFinite(fromStyle) && fromStyle > 0) return fromStyle

	const computed = Number.parseFloat(getComputedStyle(document.documentElement).zoom || '1')
	return Number.isFinite(computed) && computed > 0 ? computed : 1
}

// CSS zoom on html skews Popper offsets; divide so the later zoom pass lands on the anchor
export const uiScalePopperModifiers: UiScalePopperModifier[] = [
	{
		name: 'uiScaleZoom',
		enabled: true,
		phase: 'main',
		requires: ['popperOffsets'],
		fn: ({ state }) => {
			const zoom = getDocumentUiZoom()
			if (zoom === 1 || !state.modifiersData.popperOffsets) return

			state.modifiersData.popperOffsets.x /= zoom
			state.modifiersData.popperOffsets.y /= zoom

			const widthValue = state.styles.popper?.width
			if (widthValue) {
				const width = Number.parseFloat(String(widthValue))
				if (Number.isFinite(width)) state.styles.popper.width = `${width / zoom}px`
			}
		}
	}
]

const nearlyEqual = (a: number, b: number) => Math.abs(a - b) < 0.5

const correctPopoverPosition = (element: HTMLElement) => {
	const zoom = getDocumentUiZoom()
	if (zoom === 1) return false

	const leftN = Number.parseFloat(element.style.left)
	const topN = Number.parseFloat(element.style.top)
	let changed = false

	if (Number.isFinite(leftN)) {
		const last = Number.parseFloat(element.dataset.uiZoomLeft ?? '')
		if (!Number.isFinite(last) || !nearlyEqual(leftN, last)) {
			const corrected = leftN / zoom
			element.dataset.uiZoomLeft = String(corrected)
			element.style.left = `${corrected}px`
			changed = true
		}
	}

	if (Number.isFinite(topN)) {
		const last = Number.parseFloat(element.dataset.uiZoomTop ?? '')
		if (!Number.isFinite(last) || !nearlyEqual(topN, last)) {
			const corrected = topN / zoom
			element.dataset.uiZoomTop = String(corrected)
			element.style.top = `${corrected}px`
			changed = true
		}
	}

	return changed
}

const popoverObservers = new WeakMap<HTMLElement, MutationObserver>()

export const bindUiScalePopoverElement = (element: HTMLElement | null | undefined) => {
	if (!element || typeof MutationObserver === 'undefined') return
	if (popoverObservers.has(element)) {
		correctPopoverPosition(element)
		return
	}

	let ignoring = false

	const observer = new MutationObserver(() => {
		if (ignoring) return
		ignoring = true
		correctPopoverPosition(element)
		requestAnimationFrame(() => {
			ignoring = false
		})
	})

	observer.observe(element, { attributes: true, attributeFilter: ['style'] })
	popoverObservers.set(element, observer)
	correctPopoverPosition(element)
}

export const unbindUiScalePopoverElement = (element: HTMLElement | null | undefined) => {
	if (!element) return

	popoverObservers.get(element)?.disconnect()
	popoverObservers.delete(element)
	delete element.dataset.uiZoomLeft
	delete element.dataset.uiZoomTop
}

export const uiScalePopoverTransitionSlotProps: UiScalePopoverTransitionSlotProps = {
	onEnter: (node: HTMLElement) => bindUiScalePopoverElement(node),
	onExited: (node: HTMLElement) => unbindUiScalePopoverElement(node)
}
